interface DenotionPriority {
    denotes: string;
    priority: number;
}

export function getDenotionPriorities(): DenotionPriority[] {
    const denotionPriorities: DenotionPriority[] = [
        {
            denotes: "denotes_dnc",
            priority: 1,
        },
        {
            denotes: "denotes_callback",
            priority: 2,
        },
        {
            denotes: "denotes_appointment",
            priority: 3,
        },
        {
            denotes: "denotes_appointment_reschedule",
            priority: 4,
        },
        {
            denotes: "denotes_appointment_cancel",
            priority: 5,
        },
        {
            denotes: "denotes_appointment_confirmed",
            priority: 6,
        },
        {
            denotes: "denotes_survey",
            priority: 7,
        },
        {
            denotes: "denotes_test_call",
            priority: 8,
        },
        {
            denotes: "denotes_call_disconnect",
            priority: 8,
        },
        {
            denotes: "denotes_unreachable",
            priority: 8,
        },
        {
            denotes: "denotes_lead",
            priority: 8,
        },
        {
            denotes: "denotes_affinity",
            priority: 8,
        },
        {
            denotes: "denotes_sales_opportunity",
            priority: 8,
        },
        {
            denotes: "denotes_service_opportunity",
            priority: 8,
        },
        {
            denotes: "denotes_contact",
            priority: 9,
        },
    ];

    return denotionPriorities;
}

// Keep the first row per disposition_id so all-groups pickers do not show the same
// label twice when a disposition is linked to more than one campaign disposition group.
export function uniqueCampaignDispositionsByDispositionId<T extends { disposition_id?: number | null }>(
    dispositions: T[] | null | undefined,
): T[] {
    if (!(dispositions?.length > 0)) {
        return [];
    }

    const seen = new Set<number>();
    const unique: T[] = [];
    for (const disposition of dispositions) {
        const dispositionId = disposition?.disposition_id;
        if (!(dispositionId > 0) || seen.has(dispositionId)) {
            continue;
        }
        seen.add(dispositionId);
        unique.push(disposition);
    }
    return unique;
}

interface TotalDispositionsHolder {
    total_communication_dispositions: number | null;
}

// Ignore non-positive incoming totals so a transient null/0 from the backend doesn't leave Complete disabled after a successful save.
export function applyIncomingTotalCommunicationDispositions(
    target: TotalDispositionsHolder | null | undefined,
    value: number | null | undefined,
): void {
    if (!target) {
        return;
    }
    if (value > 0) {
        target.total_communication_dispositions = value;
    }
}

type DispositionDisplayFields = {
    disposition_color?: string | null;
    tooltip_text?: string | null;
};

export function dispositionDisplayColor(disposition: DispositionDisplayFields | null | undefined): string {
    const color = disposition?.disposition_color;
    if (!color || typeof color !== 'string') {
        return '';
    }
    return color.trim();
}

export function dispositionTooltipText(disposition: DispositionDisplayFields | null | undefined): string {
    const text = disposition?.tooltip_text;
    if (!text || typeof text !== 'string') {
        return '';
    }
    return text.trim();
}

type Rgb = { r: number; g: number; b: number };

export function dispositionContrastTextColor(hex: string): string {
    const bg = parseHexRgb(hex);
    if (!bg) {
        return 'white';
    }
    const whiteContrast = contrastRatio(bg, { r: 255, g: 255, b: 255 });
    const blackContrast = contrastRatio(bg, { r: 0, g: 0, b: 0 });
    return whiteContrast >= blackContrast ? 'white' : 'grey darken-3';
}

function parseHexRgb(hex: string): Rgb | null {
    const cleaned = (hex || '').replace(/^#/, '');
    let r: number;
    let g: number;
    let b: number;
    if (cleaned.length === 3) {
        r = parseInt(cleaned[0] + cleaned[0], 16);
        g = parseInt(cleaned[1] + cleaned[1], 16);
        b = parseInt(cleaned[2] + cleaned[2], 16);
    } else if (cleaned.length === 6) {
        r = parseInt(cleaned.slice(0, 2), 16);
        g = parseInt(cleaned.slice(2, 4), 16);
        b = parseInt(cleaned.slice(4, 6), 16);
    } else {
        return null;
    }
    if ([r, g, b].some((value) => Number.isNaN(value))) {
        return null;
    }
    return { r, g, b };
}

function channelToLinear(channel: number): number {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

function relativeLuminance({ r, g, b }: Rgb): number {
    return 0.2126 * channelToLinear(r) + 0.7152 * channelToLinear(g) + 0.0722 * channelToLinear(b);
}

function contrastRatio(a: Rgb, b: Rgb): number {
    const l1 = relativeLuminance(a);
    const l2 = relativeLuminance(b);
    const lighter = Math.max(l1, l2);
    const darker = Math.min(l1, l2);
    return (lighter + 0.05) / (darker + 0.05);
}
